import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { DenialReasonCell } from "../../../components/network/DenialReasonCell";
import { DeviceLink } from "../../../components/devices/DeviceLink";
import { Badge } from "../../../components/ui/Badge";
import { Card } from "../../../components/ui/Card";
import { DescriptionList } from "../../../components/ui/DescriptionList";
import { DomainFavicon } from "../../../components/ui/DomainFavicon";
import { Modal } from "../../../components/ui/Modal";
import { Pagination } from "../../../components/ui/Pagination";
import { SegmentedFilter } from "../../../components/ui/SegmentedFilter";
import { EmptyState, ErrorState, LoadingState } from "../../../components/ui/State";
import { type Column, Table } from "../../../components/ui/Table";
import { ApiError } from "../../../lib/api";
import {
  type NetworkEventRow,
  type URLAccessVerdict,
  networkEventsApi,
} from "../../../lib/api/network/events";
import { formatDateTime } from "../../../lib/format";
import { t } from "../../../lib/i18n";
import { useInsightsScope } from "../../../lib/useInsightsScope";

const PAGE_SIZE = 50;

type VerdictFilter = "all" | URLAccessVerdict;

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function verdictTone(verdict: URLAccessVerdict): "success" | "danger" | "info" {
  if (verdict === "allowed") return "success";
  if (verdict === "denied") return "danger";
  return "info";
}

function VerdictBadge({ verdict }: { verdict: URLAccessVerdict }) {
  return (
    <Badge tone={verdictTone(verdict)}>
      {t(`network.events.verdict.${verdict}`, verdict)}
    </Badge>
  );
}

/**
 * "All events" pivot tab — the full allow/deny/log audit stream from
 * `networkEventsApi` (docs/admin/ux-audit.md P2: "`/monitoring/network-events/`
 * has no UI at all"). Unlike "Raw events" (ViolationsPage, denials only via
 * NetworkViolationEvent), this surfaces every URLAccessEvent verdict.
 *
 * Only `from`/`to` from the workspace's shared Insights scope apply here —
 * `device_group`/`platform` are not accepted by the network-events endpoint
 * (see the filter contract documented on `NetworkEventsFilters`), so those
 * two scope controls are visibly present above this tab but don't narrow it.
 */
export function NetworkEventsTab() {
  const { from, to } = useInsightsScope();
  const [verdict, setVerdict] = useState<VerdictFilter>("all");
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [offset, setOffset] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // The date range comes from the workspace-level ScopeFilter, not a local
  // setter — reset to page 1 when it changes so a stale offset can't land on
  // an out-of-range page for the new window.
  useEffect(() => setOffset(0), [from, to]);

  const filters = useMemo(
    () => ({
      from,
      to,
      verdict: verdict === "all" ? undefined : verdict,
      limit,
      offset,
    }),
    [from, to, verdict, limit, offset],
  );

  const query = useQuery({
    queryKey: ["network-events", filters],
    queryFn: () => networkEventsApi.list(filters),
  });

  const changeVerdict = (next: VerdictFilter) => {
    setOffset(0);
    setVerdict(next);
  };

  const columns: Column<NetworkEventRow>[] = [
    {
      key: "occurred_at",
      header: t("network.events.column.occurredAt"),
      width: "11rem",
      render: (row) => (
        <span className="mono table-cell--datetime">{formatDateTime(row.occurred_at)}</span>
      ),
    },
    {
      key: "device",
      header: t("network.events.column.device"),
      render: (row) => <DeviceLink deviceId={row.device_id} deviceUid={row.device_uid} />,
    },
    {
      key: "destination",
      header: t("network.events.column.url"),
      render: (row) => (
        <div className="domain-cell">
          <DomainFavicon value={row.url_pool_value} />
          <span className="domain-name domain-name--mono">{row.url_pool_value}</span>
          <Badge>{row.url_pool_kind}</Badge>
        </div>
      ),
    },
    {
      key: "verdict",
      header: t("network.events.column.verdict"),
      render: (row) => <VerdictBadge verdict={row.verdict} />,
    },
    {
      key: "rule",
      header: t("network.events.column.policy"),
      render: (row) => <DenialReasonCell row={row} />,
    },
  ];

  return (
    <div>
      <div className="filter-bar" style={{ marginBottom: 16 }}>
        <SegmentedFilter<VerdictFilter>
          value={verdict}
          onChange={changeVerdict}
          ariaLabel={t("network.events.column.verdict")}
          options={[
            { value: "all", label: t("common.all") },
            { value: "allowed", label: t("network.events.verdict.allowed"), dot: "allow" },
            { value: "denied", label: t("network.events.verdict.denied"), dot: "block" },
            { value: "logged", label: t("network.events.verdict.logged") },
          ]}
        />
      </div>

      <Card padding="none">
        {query.isLoading && <LoadingState />}
        {query.error && (
          <ErrorState titleKey="states.errorTitle" message={translateError(query.error)} />
        )}
        {query.data && (
          <>
            <Table
              rows={query.data.items}
              columns={columns}
              rowKey={(row) => row.id}
              rowClassName="table-row--clickable"
              onRowClick={(row) => setSelectedId(row.id)}
              empty={
                <EmptyState
                  titleKey="network.events.emptyTitle"
                  bodyKey="network.events.emptyBody"
                />
              }
            />
            {query.data.total > limit && (
              <Pagination
                total={query.data.total}
                limit={limit}
                offset={offset}
                onChange={(next) => {
                  setLimit(next.limit);
                  setOffset(next.offset);
                }}
              />
            )}
          </>
        )}
      </Card>

      <NetworkEventDetailModal eventId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
}

function NetworkEventDetailModal({
  eventId,
  onClose,
}: {
  eventId: string | null;
  onClose: () => void;
}) {
  const detail = useQuery({
    queryKey: ["network-events", "detail", eventId],
    queryFn: () => networkEventsApi.get(eventId!),
    enabled: Boolean(eventId),
  });

  const event = detail.data?.event;

  return (
    <Modal
      open={Boolean(eventId)}
      onClose={onClose}
      size="lg"
      title={t("network.events.detail.title")}
    >
      {detail.isLoading && <LoadingState />}
      {detail.error && (
        <ErrorState titleKey="states.errorTitle" message={translateError(detail.error)} />
      )}
      {event && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <DescriptionList
            columns={2}
            items={[
              {
                labelKey: "network.events.column.occurredAt",
                value: <span className="mono">{formatDateTime(event.occurred_at)}</span>,
              },
              {
                labelKey: "network.events.column.device",
                value: <DeviceLink deviceId={event.device_id} deviceUid={event.device_uid} />,
              },
              {
                labelKey: "network.events.column.url",
                value: <code className="mono">{event.url_pool_value}</code>,
              },
              {
                labelKey: "network.events.column.kind",
                value: <span className="mono">{event.url_pool_kind}</span>,
              },
              {
                labelKey: "network.events.column.verdict",
                value: <VerdictBadge verdict={event.verdict} />,
              },
              {
                labelKey: "network.events.column.vuln",
                value: event.url_pool_vuln_status,
              },
              {
                labelKey: "network.events.column.policyVersion",
                value: <span className="mono">{event.policy_version}</span>,
              },
            ]}
          />
          <DenialReasonCell row={event} />
        </div>
      )}
    </Modal>
  );
}
